import type { Line, Story } from '@nelnela/contracts';

/**
 * The story reader's pure parts (M6a step 5, design `5a`).
 *
 * The reader reuses `Transcript` rather than forking the reading surface — same marks, same tap
 * targets, same gloss, one implementation of the thing this app is for. That reuse costs exactly
 * one adapter: a story sentence has to look like a `Line`. It lives here rather than in the
 * screen so the shape can be held still by a test, because everything downstream of it (marks,
 * `itemIdsOf`, the gloss's `sentenceIdx`, the exposure events) is keyed off what this returns.
 */

/** The line id of one sentence. `storyId:idx` — the only id a story sentence has. */
export const sentenceLineId = (storyId: string, idx: number): string => `${storyId}:${idx}`;

/**
 * Sentences → `Line`s.
 *
 * `startMs`/`endMs` are 0 and `speaker` is empty because a story has no timeline and no voices;
 * the reader passes `gutter="none"` so none of that is ever drawn (that prop exists for this).
 *
 * The offsets need a word. `story.sentences[].charStart` is an offset into `story.text` (the
 * sentences joined by one space, `routes/stories.ts` `layoutSentences`), and `story_item` rows
 * carry offsets of their own. A `Line`'s item offsets, though, are read against `line.text` —
 * `marksFor` slices tokens of the sentence with them — so a story-wide offset would mark the
 * wrong word, or no word, on every sentence but the first. Rather than trust one convention we
 * detect it per item: an offset that lands inside this sentence's span of `story.text` is
 * story-wide and is rebased; anything else is already sentence-local and is left alone. The two
 * readings coincide for sentence 0, which is the only case where the guess cannot be checked and
 * also the only case where it does not matter.
 */
export function storyLines(story: Pick<Story, 'id' | 'sentences' | 'items'>): Line[] {
  const byIdx = new Map(story.sentences.map((s) => [s.idx, s]));
  const bySentence = new Map<number, NonNullable<Line['items']>>();
  for (const tag of story.items ?? []) {
    const s = byIdx.get(tag.sentenceIdx);
    if (!s) continue; // a tag pointing past the end of the story marks nothing
    const local = rebase(tag.charStart, tag.charEnd, s.charStart, s.text.length);
    const list = bySentence.get(s.idx) ?? [];
    list.push({
      itemId: tag.itemId,
      kind: tag.kind,
      charStart: local ? local[0] : null,
      charEnd: local ? local[1] : null,
      // The resolver's `surface` is not on the wire for stories, so it is sliced back out of the
      // sentence. The sheet shows it; nothing computes with it.
      surface: local ? s.text.slice(local[0], local[1]) : null,
    });
    bySentence.set(s.idx, list);
  }
  return story.sentences.map((s) => {
    const items = bySentence.get(s.idx);
    return {
      id: sentenceLineId(story.id, s.idx),
      idx: s.idx,
      startMs: 0,
      endMs: 0,
      // M6b: a dialogue piece carries who says each sentence, and the reader draws it as the
      // same speaker chip Watch draws (`gutter="speaker"`). A story has no voices and passes ''.
      speaker: s.speaker ?? '',
      text: s.text,
      translation: s.translation,
      ...(items ? { items } : {}),
    };
  });
}

function rebase(
  charStart: number | null,
  charEnd: number | null,
  sentenceStart: number,
  length: number,
): [number, number] | null {
  if (charStart === null || charEnd === null) return null;
  const storyWide =
    charStart >= sentenceStart && charEnd <= sentenceStart + length && charEnd > charStart;
  const [a, b] = storyWide
    ? [charStart - sentenceStart, charEnd - sentenceStart]
    : [charStart, charEnd];
  if (a < 0 || b > length || b <= a) return null; // an offset that fits nowhere marks nothing
  return [a, b];
}

/** `5a`'s pill: "{n} working · {m} new". An imported story has no plan and shows nothing. */
export function planCounts(
  story: Pick<Story, 'itemPlan'>,
): { working: number; fresh: number } | null {
  const plan = story.itemPlan;
  if (!plan) return null;
  return { working: plan.targetItems.length, fresh: plan.newItems.length };
}

/** A story with a title of its own, or its opening words. Never an empty header. */
export function storyTitle(story: Pick<Story, 'title' | 'sentences'>): string {
  const t = story.title?.trim();
  if (t) return t;
  const first = story.sentences[0]?.text.trim() ?? '';
  return first.length > 34 ? `${first.slice(0, 34)}…` : first || 'Story';
}

/**
 * Reading time, per sentence, in the only unit that is honest: how long it was actually on
 * screen. The reader has no playhead, so there is nothing to derive dwell from — it has to be
 * measured, and measuring it means holding a clock per sentence while the learner scrolls.
 *
 * Pure and clock-injected so the awkward parts are testable without a viewport: a sentence
 * entering twice (scrolled back to) accumulates rather than restarts, a sentence that never
 * clears `minMs` is never reported at all (a flick past the middle of a story is not reading),
 * and a sentence is reported at most ONCE however many times it comes back — the second look at
 * a sentence is not a second exposure to its words.
 */
export interface DwellLedger {
  enter(idx: number, at: number): void;
  leave(idx: number, at: number): Array<{ idx: number; dwellMs: number }>;
  /** everything still on screen, at unmount / on `pagehide` */
  flush(at: number): Array<{ idx: number; dwellMs: number }>;
}

export function createDwellLedger(minMs: number): DwellLedger {
  const since = new Map<number, number>();
  const total = new Map<number, number>();
  const reported = new Set<number>();

  const settle = (idx: number, at: number): { idx: number; dwellMs: number } | null => {
    const from = since.get(idx);
    if (from === undefined) return null;
    since.delete(idx);
    const dwellMs = (total.get(idx) ?? 0) + Math.max(0, at - from);
    total.set(idx, dwellMs);
    if (reported.has(idx) || dwellMs < minMs) return null;
    reported.add(idx);
    return { idx, dwellMs };
  };

  return {
    enter(idx, at) {
      if (since.has(idx)) return; // already counting; a repeated observer callback is not a visit
      since.set(idx, at);
    },
    leave(idx, at) {
      const out = settle(idx, at);
      return out ? [out] : [];
    },
    flush(at) {
      const out: Array<{ idx: number; dwellMs: number }> = [];
      for (const idx of [...since.keys()]) {
        const one = settle(idx, at);
        if (one) out.push(one);
      }
      return out;
    },
  };
}

// ---------------------------------------------------------------------------------------------
// M6e §7 — the generating card's wait
// ---------------------------------------------------------------------------------------------

/**
 * What the generating card says while a story is being written.
 *
 * It replaces a hard-coded "~10 s" that was never measured and was wrong more often than not.
 * `medianMs` is `reviewSummary.storyGenMs` — the median of the last 30 days of successful
 * `story_gen` calls — and the seconds count UP beside it, because a number that moves is the
 * difference between "this is working" and "this has hung".
 *
 * Three rules, and each is about not lying:
 *  - No median (a new deployment, a quiet month) ⇒ no promise at all. It still counts, which is
 *    still more honest than "~10 s" was.
 *  - PAST the estimate, the estimate is dropped rather than restated. "18s of about 8s" is a
 *    broken promise printed once a second; "18s" is just the truth, and the shimmer above it is
 *    already saying that something is happening.
 *  - "about", not "~": the card is prose, and the estimate is a median of a long-tailed
 *    distribution rather than a bound.
 *
 * (Streaming the sentences as they arrive is the real fix and needs an SSE transport nothing has
 * built. It stays in ROADMAP § M9/M10; this is the whole of M6e's answer.)
 */
export function generatingWait(elapsedMs: number, medianMs: number | null): string {
  const s = Math.max(0, Math.floor(elapsedMs / 1000));
  if (medianMs === null || !Number.isFinite(medianMs) || medianMs <= 0) return `${s}s`;
  const est = Math.max(1, Math.round(medianMs / 1000));
  return s >= est ? `${s}s` : `${s}s of about ${est}s`;
}

/** The card's second line: what the wait is for, and how long it has been. */
export function generatingLine(
  working: number,
  elapsedMs: number,
  medianMs: number | null,
): string {
  // How many of the plan are NEW words is the scheduler's decision and is not knowable before the
  // story exists, so that clause is not written rather than guessed at (M6a's rule, kept).
  const what = working > 0 ? `${working} words you’re working on` : 'Weaving in your words';
  return `${what} · ${generatingWait(elapsedMs, medianMs)}`;
}

// ---------------------------------------------------------------------------------------------
// M6e §6 — the end checks' question
// ---------------------------------------------------------------------------------------------

/**
 * Is this check question written in Georgian?
 *
 * story-v4 / pieces-v7 ask for it in Georgian; every check stored before them is in English, and
 * both are read back through the same field, forever (`0008`'s rule: never rewrite stored rows).
 * So the screen decides per question rather than per prompt version: a Georgian one is rendered
 * through the reading surface and its words are tappable; an English one is plain text, exactly
 * as it was.
 *
 * Any Georgian letter is enough. A question is at most eight words and the alternative — a share
 * threshold like the generator's `GEORGIAN_LETTER_SHARE` — would turn "ვინ იყიდა Nino-ს პური?"
 * into plain text over one Latin name.
 */
export function isGeorgianQuestion(question: string): boolean {
  return /\p{Script=Georgian}/u.test(question);
}
